import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/common/Button';
import { Loader } from 'src/components/common/Loader';
import { AdBlockGateProvider, AppGateState } from 'src/context/AdBlockGateContext';
import { analytics } from 'src/utils/analytics';
import { AD_BLOCK_DETECTION_TIMEOUT_MS, detectAdBlock } from 'src/utils/detectAdBlock';

import css from './AdBlockGate.module.scss';

import responsive from 'src/styles/responsive.module.scss';

function isMobileLayout() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(responsive.mobile).matches || window.matchMedia(responsive.tabletportrait).matches;
}

function AdBlockScreen() {
  const handleReload = () => {
    analytics.gtag.event('adblock_reload_clicked');
    window.location.reload();
  };

  return (
    <main className={css.screen} dir="rtl" role="alert" aria-live="assertive">
      <div className={css.content}>
        <h1 className={css.title}>זיהינו שיש לכם חוסם פרסומות</h1>

        <p className={css.message}>להמשך השימוש במאץ׳ הפוליטי, כבו בבקשה את חוסם הפרסומות ורעננו את העמוד</p>

        <Button className={css.reloadBtn} onClick={handleReload} autoFocus>
          כיביתי, אפשר להמשיך
        </Button>
      </div>
    </main>
  );
}

type Props = PropsWithChildren<{
  enabled?: boolean;
}>;

export function AdBlockGate({ children, enabled = false }: Props) {
  const [state, setState] = useState<AppGateState>('checking');
  const reportedRef = useRef(false);

  const reportAdBlockDetected = useCallback(() => {
    if (!enabled || !isMobileLayout()) {
      return;
    }

    setState('adblock-detected');

    if (reportedRef.current) {
      return;
    }

    reportedRef.current = true;
    analytics.gtag.event('adblock_detected');
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isMobileLayout()) {
      setState('ready');
      return;
    }

    setState('checking');

    let componentIsMounted = true;
    let settled = false;

    const safetyTimer = window.setTimeout(() => {
      if (!componentIsMounted || settled) {
        return;
      }

      settled = true;
      setState(current => (current === 'checking' ? 'ready' : current));
    }, AD_BLOCK_DETECTION_TIMEOUT_MS);

    async function runDetection() {
      try {
        const result = await detectAdBlock();

        if (!componentIsMounted || settled) {
          return;
        }

        settled = true;

        if (result.blocked) {
          reportAdBlockDetected();
          return;
        }

        setState('ready');
      } catch (error) {
        console.error('Ad-block detection failed:', error);

        if (componentIsMounted && !settled) {
          settled = true;
          setState('ready');
        }
      }
    }

    void runDetection();

    return () => {
      componentIsMounted = false;
      window.clearTimeout(safetyTimer);
    };
  }, [enabled, reportAdBlockDetected]);

  return (
    <AdBlockGateProvider reportAdBlockDetected={reportAdBlockDetected}>
      <div className={enabled && state === 'adblock-detected' ? css.blockedApp : css.app}>{children}</div>

      {enabled && state === 'checking' && (
        <div className={css.loading} aria-label="טוען">
          <Loader color="white" />
        </div>
      )}

      {enabled && state === 'adblock-detected' && <AdBlockScreen />}
    </AdBlockGateProvider>
  );
}
